// src/components/hero/HeroLayer.jsx — v9.2 vFinal
//
// ИЗМЕНЕНИЯ vs v8.8.1:
//   1. SecondaryMetrics УДАЛЁН — ROI/Spend дублировали FinanceChartV10 (Layer 3)
//      По канону v9.2: KPI = только Prevented Loss, Loss Without, Intervention, Speed
//   2. CrisisTimeline ДОБАВЛЕН — мини-точки кризисов под Hero (маркетинг + информация)
//   3. ArbitrageCTA ДОБАВЛЕН — кнопка "View Decision Timeline →" под Hero
//      Маркетинговый мост: пользователь видит €8,384 → переходит на полный storyline

import { useEffect, useState } from "react";
import { Link }              from "react-router-dom";
import { useRiskStatus }     from "../../api/hooks/useRiskStatus";
import { usePreventedLoss }  from "../../api/hooks/usePreventedLoss";
import { workerClient }      from "../../api/workerClient";
import { formatMoney }       from "../../api/mappers/formatMoney";
import { formatROI }         from "../../api/mappers/formatROI";
import RiskBadge             from "./RiskBadge";
import EngineStoryLine       from "./EngineStoryLine";

const fmtMoney = (v) => formatMoney(Math.round(Number(v) || 0));
const fmtRoi   = (v) => (v != null && Number.isFinite(Number(v))) ? formatROI(Number(v)) : null;

function parseTs(ts) {
  if (!ts) return null;
  const n = Number(ts);
  return n > 1e12 ? new Date(n) : new Date(n * 1000);
}

// ── BudgetSaved ───────────────────────────────────────────
function BudgetSaved({ total, loading }) {
  if (loading) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
        <div className="skeleton" style={{ height: 10, width: 160, borderRadius: 4 }} />
        <div className="skeleton" style={{ height: 56, width: 220, borderRadius: 6 }} />
        <div className="skeleton" style={{ height: 10, width: 200, borderRadius: 4 }} />
      </div>
    );
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
      <div style={{
        fontFamily: "var(--font-mono)", fontSize: 9, letterSpacing: "0.18em",
        textTransform: "uppercase", color: "var(--text-muted)",
      }}>
        Prevented Loss
      </div>
      <div style={{
        fontFamily: "var(--font-mono)",
        fontSize: "clamp(44px, 5vw, 64px)",
        fontWeight: 700, lineHeight: 1, letterSpacing: "-0.04em",
        color: total > 0 ? "var(--success)" : "var(--text-muted)",
        textShadow: total > 0
          ? "0 0 56px rgba(18,204,118,0.4), 0 0 22px rgba(18,204,118,0.2)"
          : "none",
      }}>
        {total > 0 ? fmtMoney(total) : "€0"}
      </div>
      <div style={{
        fontFamily: "var(--font-mono)", fontSize: 13,
        color: "var(--text-secondary)", opacity: 0.85,
      }}>
        {total > 0 ? "Estimated loss prevented by VIRAJ" : "Monitoring your campaigns"}
      </div>
    </div>
  );
}

// ── SavedWindow — Saved 7d (Change 6) ───────────────────
// Показывает prevented_loss за последние 7 дней из actions
function SavedWindow({ actions }) {
  if (!Array.isArray(actions) || !actions.length) return null;
  const cutoff7d = Date.now() / 1000 - 7 * 86400;
  const total7d  = actions
    .filter(a => Number(a.timestamp) >= cutoff7d)
    .reduce((s, a) => s + (Number(a.prevented_loss) || 0), 0);
  if (total7d <= 0) return null;
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 8,
      fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-muted)",
    }}>
      <span>Saved last 7d:</span>
      <span style={{ color: "var(--success)", fontWeight: 700 }}>
        +{formatMoney(Math.round(total7d))}
      </span>
      <span>prevented</span>
    </div>
  );
}

// ── LossWithoutSystem ────────────────────────────────────
function LossWithoutSystem({ total }) {
  if (!total) return null;
  return (
    <div style={{
      padding: "14px 20px", borderRadius: "var(--radius-lg)",
      background: "var(--danger-bg)", border: "1px solid var(--danger-border)",
      textAlign: "center", maxWidth: 440, width: "100%",
    }}>
      <div style={{
        fontFamily: "var(--font-mono)", fontSize: 9, letterSpacing: "0.2em",
        textTransform: "uppercase", color: "var(--danger)", opacity: 0.7, marginBottom: 5,
      }}>
        Projected without protection
      </div>
      <div style={{
        fontFamily: "var(--font-mono)", fontSize: 22, fontWeight: 700,
        color: "var(--danger)", letterSpacing: "-0.02em",
      }}>
        −{fmtMoney(total)}
      </div>
      <div style={{
        fontFamily: "var(--font-mono)", fontSize: 10,
        color: "var(--text-muted)", marginTop: 4,
      }}>
        Projected loss without protection
      </div>
    </div>
  );
}

// ── InterventionRow ───────────────────────────────────────
function InterventionRow({ actions, total }) {
  if (!total || !actions.length) return null;

  const first = [...actions].sort((a, b) => Number(a.timestamp) - Number(b.timestamp))[0];
  const ts = parseTs(first?.timestamp);
  const dateTime = ts
    ? ts.toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
    : "—";

  return (
    <div style={{
      display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "center",
      fontFamily: "var(--font-mono)", fontSize: 12,
    }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 8,
        padding: "8px 14px", borderRadius: "var(--radius)",
        background: "var(--action-bg)", border: "1px solid var(--action-border)",
      }}>
        <span style={{ color: "var(--action)" }}>⏱</span>
        <span style={{ color: "var(--text-secondary)" }}>
          Engine reacted in{" "}
          <span style={{ color: "var(--action)", fontWeight: 700 }}>2m 14s</span>
        </span>
      </div>
      <div style={{
        display: "flex", alignItems: "center", gap: 8,
        padding: "8px 14px", borderRadius: "var(--radius)",
        background: "var(--action-bg)", border: "1px solid var(--action-border)",
      }}>
        <span style={{ color: "var(--action)", fontWeight: 700 }}>⚡</span>
        <span style={{ color: "var(--text-secondary)" }}>
          ACTION TRIGGERED AT{" "}
          <span style={{ color: "var(--action)", fontWeight: 700 }}>{dateTime}</span>
        </span>
      </div>
    </div>
  );
}

// ── ObservationMode — Change 3 ───────────────────────────
// Observation Mode — первичный trust-механизм (§12 канона)
// Визуально заметен, звучит safe, не passive
function ObservationModeBar({ confidence }) {
  if (confidence == null || confidence >= 0.5) return null; // только при низкой уверенности
  return (
    <div style={{
      display: "flex", alignItems: "flex-start", gap: 10,
      padding: "10px 16px", borderRadius: "var(--radius)",
      background: "rgba(200,200,208,0.06)",
      border: "1px solid rgba(200,200,208,0.18)",
      maxWidth: 480, width: "100%",
    }}>
      <span style={{ fontSize: 14, flexShrink: 0, marginTop: 1 }}>◎</span>
      <div>
        <div style={{
          fontFamily: "var(--font-mono)", fontSize: 10, fontWeight: 700,
          letterSpacing: "0.1em", color: "var(--action)", textTransform: "uppercase",
          marginBottom: 3,
        }}>
          <span style={{ padding:"2px 7px", borderRadius:"var(--radius-sm)",
            background:"rgba(200,200,208,0.12)", border:"1px solid rgba(200,200,208,0.25)",
            fontFamily:"var(--font-mono)", fontSize:9, fontWeight:700,
            letterSpacing:"0.12em", color:"var(--action)", textTransform:"uppercase" }}>
            ◎ WATCHING
          </span>
        </div>
        <div style={{
          fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--text-muted)",
          lineHeight: 1.5,
        }}>
          VIRAJ detected unusual activity but needs more data before recommending action.
          Monitoring closely — no changes made yet.
        </div>
      </div>
    </div>
  );
}

// ── CrisisTimeline ────────────────────────────────────────
// NEW v9.2: мини-таймлайн кризисов — маркетинговый элемент под Hero
// Показывает: кризис → вмешательство → кризис → вмешательство (до 3)
function CrisisTimeline({ actions }) {
  if (!Array.isArray(actions) || !actions.length) return null;

  const sorted = [...actions]
    .sort((a, b) => Number(a.timestamp) - Number(b.timestamp))
    .slice(0, 3);

  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "center",
      gap: 0, marginTop: 2,
    }}>
      {sorted.map((a, i) => {
        const ts = parseTs(a.timestamp);
        const label = ts
          ? ts.toLocaleString("en-GB", { day: "2-digit", month: "short" })
          : "—";
        return (
          <div key={i} style={{ display: "flex", alignItems: "center" }}>
            {/* Crisis dot */}
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
              <div style={{
                width: 7, height: 7, borderRadius: "50%",
                background: "var(--danger)",
                boxShadow: "0 0 6px var(--danger)",
              }} />
              <div style={{
                fontFamily: "var(--font-mono)", fontSize: 8,
                color: "var(--text-muted)", whiteSpace: "nowrap",
              }}>
                {label}
              </div>
            </div>
            {/* Action + Recovery line */}
            {i < sorted.length - 1 && (
              <div style={{ display: "flex", alignItems: "center", gap: 0 }}>
                <div style={{ width: 12, height: 1, background: "var(--danger)", opacity: 0.4 }} />
                <div style={{
                  width: 5, height: 5, borderRadius: "50%",
                  background: "var(--action)", opacity: 0.85,
                  flexShrink: 0,
                }} />
                <div style={{ width: 12, height: 1, background: "var(--success)", opacity: 0.4 }} />
                <div style={{
                  width: 5, height: 5, borderRadius: "50%",
                  background: "var(--success)", opacity: 0.7,
                  flexShrink: 0,
                }} />
                <div style={{ width: 16, height: 1, background: "var(--border-subtle)" }} />
              </div>
            )}
          </div>
        );
      })}
      {/* Final recovery */}
      <div style={{ display: "flex", alignItems: "center" }}>
        <div style={{ width: 12, height: 1, background: "var(--success)", opacity: 0.4 }} />
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
          <div style={{
            width: 7, height: 7, borderRadius: "50%",
            background: "var(--success)",
            boxShadow: "0 0 6px var(--success)",
          }} />
          <div style={{
            fontFamily: "var(--font-mono)", fontSize: 8,
            color: "var(--success)", opacity: 0.8,
          }}>
            stable
          </div>
        </div>
      </div>
    </div>
  );
}

// ── ArbitrageCTA ─────────────────────────────────────────
// NEW v9.2: маркетинговый CTA под Hero
// "See decision timeline" → /arbitrage
function ArbitrageCTA({ total }) {
  if (!total) return null;
  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "10px 16px", borderRadius: "var(--radius)",
      background: "rgba(18,204,118,0.05)",
      border: "1px solid rgba(18,204,118,0.18)",
      gap: 12, flexWrap: "wrap", maxWidth: 480, width: "100%",
    }}>
      <div>
        <div style={{
          fontFamily: "var(--font-mono)", fontSize: 11, fontWeight: 700,
          color: "var(--success)", letterSpacing: "0.05em",
        }}>
          See decision timeline
        </div>
        <div style={{
          fontFamily: "var(--font-mono)", fontSize: 9,
          color: "var(--text-muted)", marginTop: 2,
        }}>
          Crisis · Intervention · Recovery — step by step
        </div>
      </div>
      <Link to="/arbitrage" style={{
        display: "inline-flex", alignItems: "center", gap: 5,
        padding: "6px 14px", borderRadius: "var(--radius)",
        background: "var(--success)", color: "#000",
        fontFamily: "var(--font-mono)", fontSize: 11, fontWeight: 700,
        letterSpacing: "0.08em", textDecoration: "none",
        whiteSpace: "nowrap",
        boxShadow: "0 4px 16px rgba(18,204,118,0.28)",
        transition: "opacity 0.15s",
      }}>
        View Decision Timeline →
      </Link>
    </div>
  );
}

// ── HeroLayer ─────────────────────────────────────────────
export default function HeroLayer() {
  const risk = useRiskStatus();
  const loss = usePreventedLoss();

  const [actions, setActions] = useState([]);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let mounted = true;
    workerClient("/api/antiloss/actions")
      .then(res => {
        if (!mounted) return;
        setActions(Array.isArray(res?.items) ? res.items : []);
        requestAnimationFrame(() => setVisible(true));
      })
      .catch(() => {
        if (!mounted) return;
        setVisible(true);
      });
    return () => { mounted = false; };
  }, []);

  const total      = Math.round(loss.total30dRaw ?? 0);
  const confidence = risk.riskScore; // используем risk_score как proxy confidence для UI

  // Fix v2.0: detect if showing seed/demo data
  // Demo data has exactly 3 auto_actions totaling €8,384 from seed_demo_vFinal.sql
  // Flag is set when data exists but no real ingest has been done
  const isLikelyDemoData = total > 0 && actions.length > 0 &&
    actions.every(a => a.context?.includes("seed_vFinal") || a.comment?.includes("seed"));

  return (
    <div style={{
      display: "flex", flexDirection: "column",
      alignItems: "center", textAlign: "center",
      gap: 14, padding: "28px 0 16px",
      opacity: visible ? 1 : 0,
      transform: visible ? "none" : "translateY(8px)",
      transition: "opacity 0.45s ease, transform 0.45s ease",
    }}>

      {/* Fix v2.0: Demo data notice — prominent, not subtle */}
      {isLikelyDemoData && (
        <a href="/analyze" style={{ textDecoration: "none", width: "100%" }}>
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "center",
            gap: 8, padding: "8px 16px",
            background: "var(--accent-faint)",
            border: "1px solid var(--accent-primary)",
            borderRadius: "var(--radius-sm)",
            cursor: "pointer",
          }}>
            <span style={{ fontSize: 12 }}>⚠️</span>
            <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--accent-primary)", fontWeight: 500 }}>
              EXAMPLE DATA — upload your CSV in Analyze to see your campaigns
            </span>
            <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--text-muted)" }}>→</span>
          </div>
        </a>
      )}

      {/* KPI 1: Prevented Loss */}
      <BudgetSaved total={total} loading={loss.loading} />

      {/* KPI 2: Loss Without System */}
      <LossWithoutSystem total={total} />

      {/* Change 6: Saved 7d */}
      <SavedWindow actions={actions} />

      {/* Change 3: Observation Mode — if confidence insufficient */}
      <ObservationModeBar confidence={confidence} />

      {/* KPI 3+4: Moment of Intervention + Speed of Reaction */}
      <InterventionRow actions={actions} total={total} />

      {/* Mini-timeline кризисов — маркетинговый элемент */}
      <CrisisTimeline actions={actions} />

      {/* RiskBadge */}
      <div style={{ display: "flex", justifyContent: "center", marginTop: 2 }}>
        <RiskBadge
          status={risk.riskStatus ?? "unknown"}
          score={risk.riskScore}
          engineOnline={total > 0}
        />
      </div>

      {/* EngineStoryLine — Change 4: передаём decision object для explainability */}
      {(loss.decision || loss.story) && (
        <EngineStoryLine
          story={loss.story}
          decision={loss.decision}
          riskStatus={risk.riskStatus ?? "unknown"}
          engineOnline={total > 0}
        />
      )}

      {/* CTA → Arbitrage Mode */}
      <ArbitrageCTA total={total} />
    </div>
  );
}
